/**
 * RecallCentralFillOrdersModal (POM-5410/5411/5412)
 *
 * Thin wrapper over the shared CentralFillOrdersActionModal supplying the
 * Recall copy + API. Recall syncs with the external compounding pharmacy, so its
 * note is shared with the pharmacy and the action is destructive.
 */
import PropTypes from 'prop-types';

import { recallCentralFillOrdersApi } from 'api/centralFill';
import CentralFillOrdersActionModal from './CentralFillOrdersActionModal';

const RecallCentralFillOrdersModal = (props) => {
  const { open, orders, onClose, onProcessed } = props;

  return (
    <CentralFillOrdersActionModal
      open={open}
      orders={orders}
      onClose={onClose}
      onProcessed={onProcessed}
      idPrefix='recall'
      title='Recall Compounding Orders'
      subtitle='Review the selected orders before recalling them from the compounding pharmacy.'
      warningText='Selected orders will be removed from the compounding pharmacy workflow.'
      noteLabel='Recall Note'
      noteHelperText='This note will be shared with the compounding pharmacy as the reason for recall.'
      notePlaceholder='Reason for recalling these orders…'
      noteAriaLabel='recall note'
      primaryColor='error'
      getPrimaryLabel={(count) => `Recall ${count} ${count === 1 ? 'Order' : 'Orders'}`}
      confirmTitle='Confirm Recall'
      getConfirmCountText={(count) =>
        `${count === 1 ? 'Order' : 'Orders'} will be recalled from the compounding pharmacy`
      }
      consequences={[
        'Removed from the compounding pharmacy workflow',
        'The pharmacy is notified of the recall',
        'This action cannot be undone',
      ]}
      notePreviewLabel='Note to pharmacy'
      action={recallCentralFillOrdersApi}
      getSuccessMessage={(successCount) =>
        `${successCount} order(s) submitted for recall. The compounding pharmacy will be notified shortly.`
      }
      getPartialMessage={(successCount, failureCount) =>
        `${successCount} order(s) submitted for recall. ${failureCount} order(s) failed to submit request — please retry those.`
      }
      getErrorMessage={() =>
        'Failed to submit recall requests. Please try again or contact support.'
      }
    />
  );
};

RecallCentralFillOrdersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      trackingId: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      patientName: PropTypes.string,
      drugName: PropTypes.string,
      partnerName: PropTypes.string,
      compoundingPharmacyName: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onProcessed: PropTypes.func.isRequired,
};

export default RecallCentralFillOrdersModal;
